import React, { useState } from "react";
import {
  Card,
  Tabs,
  Typography,
  Space,
  Row,
  Col,
  Button,
  Select,
  Progress,
} from "antd";

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

export default function TransformersExplained() {
  const [demoInput, setDemoInput] = useState("The cat sat on the");
  const [attentionStep, setAttentionStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [output, setOutput] = useState("");
  const [demoMode, setDemoMode] = useState("next-word");
  const [generatedText, setGeneratedText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [promptInput, setPromptInput] = useState("");
  const [hoveredWord, setHoveredWord] = useState(null);

  const predictNext = async () => {
    setIsProcessing(true);
    setOutput("");

    const steps = [
      "Tokenizing input...",
      "Computing self-attention...",
      "Processing through layers...",
      "Generating prediction...",
    ];

    for (let i = 0; i < steps.length; i++) {
      setAttentionStep(i);
      await new Promise((resolve) => setTimeout(resolve, 800));
    }

    const completions = {
      "The cat sat on the": "mat",
      "Hello, how are": "you",
      "I love to": "learn",
      "Artificial intelligence is": "amazing",
      "The weather today is": "beautiful",
      "Once upon a": "time",
      "The future of AI": "looks",
      "Machine learning helps": "us",
    };

    const result = completions[demoInput] || "next";
    setOutput(result);
    setIsProcessing(false);
    setAttentionStep(0);
  };

  const generateContent = async () => {
    if (!promptInput.trim()) return;

    setIsGenerating(true);
    setGeneratedText("");
    setGenerationProgress(0);

    const responses = {
      "what is hangry dogs?":
        "Hangry Dogs is a thrilling card game of offense and defense where 2-6 players compete for victory. It's a great game to be played with friends and family. It has many dog-like characters and the game has inspired writing AI Made Human book.",
      "write a short story about a robot":
        "In a bustling city of the future, a small robot named Chip discovered something remarkable: the ability to dream. Unlike other robots who simply executed their programming, Chip experienced vivid visions during his charging cycles. One night, he dreamed of a world where robots and humans lived in perfect harmony, creating art and music together. When he woke, Chip decided to make this dream a reality. He began painting murals on the city walls, each one depicting his vision of unity. Slowly, other robots and humans stopped to admire his work, and conversations began. What started as one robot's dream became a movement that transformed the entire city.",
      "explain quantum computing simply":
        'Imagine a regular computer as someone walking through a maze, trying one path at a time until they find the exit. A quantum computer, on the other hand, is like a ghost that can walk through all paths simultaneously and instantly know which one leads to the exit. Regular computers use bits that are either 0 or 1, like a light switch that\'s either on or off. Quantum computers use quantum bits or "qubits" that can be both 0 and 1 at the same time, thanks to a property called superposition. This allows them to process massive amounts of information simultaneously, making them incredibly powerful for certain types of problems like drug discovery, encryption, and complex simulations.',
      "create a poem about the ocean":
        "Beneath the surface, deep and blue,\nWhere ancient secrets hide from view,\nThe ocean whispers tales untold,\nOf treasures lost and mysteries bold.\n\nWaves that dance with moonlit grace,\nCaress the shore in soft embrace,\nWhile currents flow with purpose clear,\nThrough depths that few will ever near.\n\nCreatures glide through liquid space,\nIn this vast and wondrous place,\nWhere life began so long ago,\nAnd still, new wonders grow and grow.",
      "write code to sort an array":
        "Here's a simple implementation of the QuickSort algorithm in Python:\n\n```python\ndef quicksort(arr):\n    if len(arr) <= 1:\n        return arr\n    \n    pivot = arr[len(arr) // 2]\n    left = [x for x in arr if x < pivot]\n    middle = [x for x in arr if x == pivot]\n    right = [x for x in arr if x > pivot]\n    \n    return quicksort(left) + middle + quicksort(right)\n\n# Example usage\nnumbers = [64, 34, 25, 12, 22, 11, 90]\nsorted_numbers = quicksort(numbers)\nprint(sorted_numbers)  # Output: [11, 12, 22, 25, 34, 64, 90]\n```\n\nThis recursive algorithm picks a pivot element and partitions the array into elements smaller and larger than the pivot, then recursively sorts each partition.",
    };

    const response =
      responses[promptInput.toLowerCase()] ||
      `Transformers are powerful AI models that can generate creative and contextual content. Based on your prompt "${promptInput}", a real Transformer model would analyze the context, understand your intent, and generate relevant, coherent text. This could be stories, explanations, code, poetry, or any other type of content. The model uses its training on vast amounts of text to predict not just the next word, but entire sequences that make sense together, maintaining context and style throughout the generation.`;

    const words = response.split(" ");
    for (let i = 0; i < words.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      setGeneratedText((prev) => prev + (i === 0 ? "" : " ") + words[i]);
      setGenerationProgress(((i + 1) / words.length) * 100);
    }

    setIsGenerating(false);
  };

  const AttentionVisualization = () => {
    const words = demoInput.split(" ");

    return (
      <div>
        <Paragraph style={{ marginBottom: "16px" }}>
          Hover over words to see how the model pays attention to different
          parts of the input
        </Paragraph>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            justifyContent: "center",
            marginBottom: "24px",
            position: "relative",
            minHeight: "80px",
          }}
        >
          {words.map((word, idx) => (
            <div
              key={idx}
              style={{ position: "relative", zIndex: 10 }}
              onMouseEnter={() => setHoveredWord(idx)}
              onMouseLeave={() => setHoveredWord(null)}
            >
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.3s",
                  background: hoveredWord === idx ? "#0071e3" : "#fff",
                  color: hoveredWord === idx ? "#fff" : "#000",
                  border: `2px solid ${
                    hoveredWord === idx ? "#0071e3" : "#d9d9d9"
                  }`,
                  transform: hoveredWord === idx ? "scale(1.1)" : "scale(1)",
                  fontWeight: hoveredWord === idx ? 600 : 400,
                }}
              >
                {word}
              </div>
              {hoveredWord === idx && (
                <svg
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "400px",
                    height: "100px",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                >
                  {words.map((_, targetIdx) => {
                    if (targetIdx === idx) return null;
                    const distance = Math.abs(targetIdx - idx);
                    const opacity = Math.max(0.2, 1 - distance * 0.15);
                    const strokeWidth = Math.max(1, 4 - distance);
                    const xOffset = (targetIdx - idx) * 60;
                    const yHeight = distance * 15;

                    return (
                      <path
                        key={targetIdx}
                        d={`M 200 0 Q ${200 + xOffset / 2} ${yHeight} ${
                          200 + xOffset
                        } 0`}
                        stroke="#0071e3"
                        strokeWidth={strokeWidth}
                        fill="none"
                        opacity={opacity}
                      />
                    );
                  })}
                </svg>
              )}
            </div>
          ))}
        </div>
        <Paragraph style={{ marginBottom: 0, fontSize: "14px" }}>
          <Text strong>What's happening?</Text> Each word "looks at" all other
          words to understand context. Words that are more relevant get more
          attention (shown by the curved lines). This is how the model
          understands relationships and meaning.
        </Paragraph>
      </div>
    );
  };

  const renderIntro = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={<span style={{ color: "#0071e3" }}>What is a Transformer?</span>}
      >
        <Paragraph>
          Imagine you are reading a book. To understand a sentence, you don't
          just read one word at a time in isolation. You look at all the words
          together, understanding how they relate to each other. That's exactly
          what a Transformer does!
        </Paragraph>

        <Paragraph>
          A Transformer is a type of artificial intelligence model that
          processes information by paying attention to different parts of the
          input at the same time. It's like having multiple spotlights that can
          focus on different words simultaneously, understanding how they
          connect and what they mean together.
        </Paragraph>

        <Paragraph>
          Transformers revolutionized AI by using self-attention to process
          information in parallel, enabling the creation of powerful models that
          can understand and generate human-like text, images, and more. From
          powering ChatGPT to generating images, Transformers are the technology
          behind the AI revolution we are experiencing today.
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Why It Matters</span>}>
        <Paragraph style={{ marginBottom: 0 }}>
          Transformers power ChatGPT, Google Translate, image generators, and
          countless other AI tools you use daily.
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>The Breakthrough</span>}>
        <Paragraph style={{ marginBottom: 0 }}>
          Before Transformers, AI models struggled with long sequences.
          Transformers changed everything by processing all information at once.
        </Paragraph>
      </Card>
    </Space>
  );

  const renderHowItWorks = () => {
    const steps = [
      {
        step: "1",
        title: "Tokenization",
        description:
          'First, the given sentence is broken into words, referred as "tokens". The sentence "I love Hangry Dogs" becomes four tokens: ["I", "love", "Hangry", "Dogs"].',
      },
      {
        step: "2",
        title: "Positional Encoding",
        description:
          'Since the model looks at all words at once, we need to tell it the order. It\'s like numbering seats in a theater - "love" comes after "I" and before "Dogs".',
      },
      {
        step: "3",
        title: "Self-Attention (The Magic Part!)",
        description:
          'This is where the magic happens! Each word looks at every other word and asks: "How relevant are you to understanding me?" For example, in "The cat sat on the mat", when processing "sat", the model pays strong attention to "cat" (who sat?) and "mat" (where?).',
        example:
          'In the sentence "The animal didn\'t cross the street because it was too tired", the model learns that "it" refers to "animal" (not "street") by paying more attention to that connection.',
      },
      {
        step: "4",
        title: "Feed Forward",
        description:
          "After attention, each token goes through a neural network that processes the information further, adding more understanding and complexity.",
      },
      {
        step: "5",
        title: "Multiple Layers",
        description:
          "This whole process repeats many times (often 12-96 layers!). Each layer adds a deeper level of understanding. Early layers might understand grammar, while deeper layers understand meaning and context.",
      },
      {
        step: "6",
        title: "Output Generation",
        description:
          "Finally, the model produces output - whether that's the next word in a sentence, a complete paragraph, a translation, or an answer to your question.",
      },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {steps.map((item, idx) => (
          <Card
            key={idx}
            title={
              <span style={{ color: "#0071e3" }}>
                Step {item.step}: {item.title}
              </span>
            }
          >
            <Paragraph style={{ marginBottom: item.example ? "12px" : 0 }}>
              {item.description}
            </Paragraph>
            {item.example && (
              <div
                style={{
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #0071e3",
                }}
              >
                <Paragraph
                  style={{
                    fontWeight: 600,
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  Real Example:
                </Paragraph>
                <Paragraph style={{ marginBottom: 0, fontSize: "14px" }}>
                  {item.example}
                </Paragraph>
              </div>
            )}
          </Card>
        ))}

        <Card
          title={
            <span style={{ color: "#0071e3" }}>
              Key Innovation: Parallel Processing
            </span>
          }
        >
          <Paragraph style={{ marginBottom: 0 }}>
            Unlike older models that read word by word (slow!), Transformers
            process all words simultaneously. It's like reading an entire page
            at once instead of one word at a time. This makes them incredibly
            fast and powerful!
          </Paragraph>
        </Card>
      </Space>
    );
  };

  const renderDemo = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={<span style={{ color: "#0071e3" }}>Two Demonstrations</span>}
      >
        <Paragraph>
          Transformers do more than just predict the next word - they generate
          entire sequences of text, understand context, and create coherent
          content!
        </Paragraph>
        <Paragraph>Choose one of the following.</Paragraph>
        <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
          <div
            onClick={() => setDemoMode("next-word")}
            style={{
              flex: 1,
              padding: "12px 16px",
              textAlign: "center",
              cursor: "pointer",
              borderRadius: "6px",
              border: `2px solid ${
                demoMode === "next-word" ? "#1890ff" : "#d9d9d9"
              }`,
              backgroundColor: demoMode === "next-word" ? "#1890ff" : "white",
              color: demoMode === "next-word" ? "white" : "inherit",
              fontWeight: "500",
            }}
          >
            Next Word Prediction
          </div>
          <div
            onClick={() => setDemoMode("generate")}
            style={{
              flex: 1,
              padding: "12px 16px",
              textAlign: "center",
              cursor: "pointer",
              borderRadius: "6px",
              border: `2px solid ${
                demoMode === "generate" ? "#1890ff" : "#d9d9d9"
              }`,
              backgroundColor: demoMode === "generate" ? "#1890ff" : "white",
              color: demoMode === "generate" ? "white" : "inherit",
              fontWeight: "500",
            }}
          >
            Content Generation
          </div>
        </div>
      </Card>

      {demoMode === "next-word" && (
        <Card
          title={
            <span style={{ color: "#0071e3" }}>
              How Next-Word Prediction Works
            </span>
          }
        >
          <AttentionVisualization />
          <br />
          <Paragraph style={{ marginBottom: "16px" }}>
            This is the foundation of how Transformers work. The model predicts
            the most likely next word based on all previous words. By repeating
            this process, it can generate entire sentences and paragraphs!
          </Paragraph>

          <Select
            value={demoInput}
            onChange={setDemoInput}
            style={{ width: "100%", marginBottom: "16px" }}
            size="large"
          >
            <Option value="The cat sat on the">The cat sat on the</Option>
            <Option value="Hello, how are">Hello, how are</Option>
            <Option value="I love to">I love to</Option>
            <Option value="Artificial intelligence is">
              Artificial intelligence is
            </Option>
            <Option value="The weather today is">The weather today is</Option>
            <Option value="Once upon a">Once upon a</Option>
            <Option value="The future of AI">The future of AI</Option>
            <Option value="Machine learning helps">
              Machine learning helps
            </Option>
          </Select>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              type="primary"
              onClick={predictNext}
              disabled={isProcessing}
              size="large"
            >
              {isProcessing ? "Processing..." : "Predict Next Word"}
            </Button>
          </div>

          {isProcessing && (
            <Card style={{ marginTop: "16px" }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>
                  {attentionStep === 0 && "Tokenizing input..."}
                  {attentionStep === 1 && "Computing self-attention..."}
                  {attentionStep === 2 && "Processing through layers..."}
                  {attentionStep === 3 && "Generating prediction..."}
                </Text>
                <Progress percent={(attentionStep + 1) * 25} status="active" />
              </Space>
            </Card>
          )}

          {output && !isProcessing && (
            <Card
              style={{
                marginTop: "16px",
                borderColor: "#52c41a",
                borderWidth: "1px",
              }}
            >
              <Title level={5}>Prediction Result:</Title>
              <Paragraph style={{ fontSize: "16px", marginBottom: "12px" }}>
                {demoInput}{" "}
                <Text
                  style={{ fontSize: "16px", color: "green", fontWeight: 500 }}
                >
                  {output}
                </Text>
              </Paragraph>
            </Card>
          )}
        </Card>
      )}

      {demoMode === "generate" && (
        <Card
          title={
            <span style={{ color: "#0071e3" }}>Full Content Generation</span>
          }
        >
          <Paragraph style={{ marginBottom: "16px" }}>
            Transformers can generate complete, coherent content - not just one
            word at a time! They predict multiple words in sequence, maintaining
            context and style throughout. This is how ChatGPT writes essays,
            generates code, creates stories, and more.
          </Paragraph>

          <Select
            value={promptInput}
            onChange={setPromptInput}
            placeholder="Choose a prompt to see content generation"
            style={{ width: "100%", marginBottom: "16px" }}
            size="large"
          >
            <Option value="what is hangry dogs?">What is Hangry Dogs?</Option>
            <Option value="write a short story about a robot">
              Write a short story about a robot
            </Option>
            <Option value="explain quantum computing simply">
              Explain quantum computing simply
            </Option>
            <Option value="create a poem about the ocean">
              Create a poem about the ocean
            </Option>
            <Option value="write code to sort an array">
              Write code to sort an array
            </Option>
          </Select>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              type="primary"
              onClick={generateContent}
              disabled={isGenerating || !promptInput}
              size="large"
            >
              {isGenerating ? "Generating..." : "Generate Content"}
            </Button>
          </div>

          {isGenerating && (
            <Card style={{ marginTop: "16px" }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>
                  Generating content... ({Math.round(generationProgress)}%)
                </Text>
                <Progress
                  percent={Math.round(generationProgress)}
                  status="active"
                />
              </Space>
            </Card>
          )}

          {generatedText && !isGenerating && (
            <Card
              style={{
                marginTop: "16px",
                borderColor: "#52c41a",
                borderWidth: "1px",
              }}
            >
              <Title level={5}>Generated Content:</Title>
              <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                {generatedText}
              </Paragraph>
            </Card>
          )}
        </Card>
      )}
    </Space>
  );

  const renderHistory = () => {
    const timeline = [
      {
        year: "2017",
        title: '"Attention Is All You Need"',
        content:
          "Researchers at Google (Vaswani et al.) published a groundbreaking paper introducing the Transformer architecture. The title itself was bold - they claimed attention mechanisms alone were sufficient, without the recurrent networks everyone was using. The paper has been cited over 100,000 times and fundamentally changed AI research!",
      },
      {
        year: "2018",
        title: "BERT",
        content:
          "Google released BERT (Bidirectional Encoder Representations from Transformers), which could understand context from both directions. It revolutionized natural language understanding tasks.",
      },
      {
        year: "2019",
        title: "GPT-2",
        content:
          "OpenAI released GPT-2, which could generate remarkably human-like text. It was so good that they initially withheld the full model due to concerns about misuse.",
      },
      {
        year: "2020",
        title: "GPT-3",
        content:
          "GPT-3 launched with 175 billion parameters, showing that scaling up Transformers led to emergent abilities - capabilities that smaller models didn't have.",
      },
      {
        year: "2021",
        title: "Vision Transformers",
        content:
          "Researchers showed Transformers weren't just for text - they could process images too! Vision Transformers (ViT) began competing with traditional computer vision models.",
      },
      {
        year: "2022",
        title: "ChatGPT Launch",
        content:
          "OpenAI released ChatGPT, bringing Transformer technology to millions of users and sparking global interest in AI. It reached 100 million users in just 2 months!",
      },
      {
        year: "2023-2024",
        title: "Multimodal Era",
        content:
          "GPT-4, Claude, Gemini, and other models can now understand and generate text, images, code, and more. Transformers power nearly every major AI breakthrough.",
      },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {timeline.map((item, idx) => (
          <Card
            key={idx}
            title={
              <span style={{ color: "#0071e3" }}>
                {item.year} - {item.title}
              </span>
            }
          >
            <Paragraph style={{ marginBottom: 0 }}>{item.content}</Paragraph>
          </Card>
        ))}

        <Card
          title={<span style={{ color: "#0071e3" }}>Why This Mattered</span>}
        >
          <Paragraph style={{ marginBottom: 0 }}>
            Before Transformers, AI models struggled with long-range
            dependencies and were slow to train. Transformers solved both
            problems, enabling the AI revolution we're experiencing today. They
            made it possible to train much larger models on much more data,
            leading to the powerful AI assistants and tools we use every day.
          </Paragraph>
        </Card>
      </Space>
    );
  };

  const renderUseCases = () => {
    const categories = [
      {
        title: "Natural Language Processing",
        items: [
          {
            bold: "ChatGPT & AI Assistants:",
            text: "Conversational AI that helps with writing, coding, and answering questions",
          },
          {
            bold: "Translation:",
            text: "Google Translate, DeepL - breaking language barriers worldwide",
          },
          {
            bold: "Search Engines:",
            text: "Google and Bing use Transformers to understand search queries better",
          },
          {
            bold: "Content Creation:",
            text: "Writing articles, emails, marketing copy, and more",
          },
        ],
      },
      {
        title: "Computer Vision",
        items: [
          {
            bold: "Image Generation:",
            text: "DALL-E, Midjourney, Stable Diffusion - creating images from text",
          },
          {
            bold: "Medical Imaging:",
            text: "Detecting diseases in X-rays and MRI scans",
          },
          {
            bold: "Autonomous Vehicles:",
            text: "Understanding and navigating the visual world",
          },
          {
            bold: "Photo Editing:",
            text: "Smart object removal, enhancement, and manipulation",
          },
        ],
      },
      {
        title: "Audio & Music",
        items: [
          {
            bold: "Speech Recognition:",
            text: "Siri, Alexa, Google Assistant understanding your voice",
          },
          {
            bold: "Music Generation:",
            text: "AI creating original songs and soundtracks",
          },
          {
            bold: "Voice Cloning:",
            text: "Text-to-speech with natural-sounding voices",
          },
          {
            bold: "Audio Transcription:",
            text: "Converting speech to text automatically",
          },
        ],
      },
      {
        title: "Video & Multimodal",
        items: [
          {
            bold: "Video Generation:",
            text: "Creating videos from text descriptions (Sora, Gen-2)",
          },
          {
            bold: "Video Understanding:",
            text: "Summarizing and analyzing video content",
          },
          {
            bold: "Content Moderation:",
            text: "Detecting inappropriate content automatically",
          },
          {
            bold: "Visual Question Answering:",
            text: "AI answering questions about images and videos",
          },
        ],
      },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {categories.map((category, idx) => (
          <Card
            key={idx}
            title={<span style={{ color: "#0071e3" }}>{category.title}</span>}
          >
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {category.items.map((item, itemIdx) => (
                <Paragraph key={itemIdx} style={{ marginBottom: 0 }}>
                  <Text strong>{item.bold}</Text> {item.text}
                </Paragraph>
              ))}
            </Space>
          </Card>
        ))}

        <Card
          title={
            <span style={{ color: "#0071e3" }}>
              Other Exciting Applications
            </span>
          }
        >
          <Row gutter={[16, 16]}>
            {[
              {
                title: "Science & Research",
                items: [
                  "Protein folding prediction (AlphaFold)",
                  "Drug discovery and molecular design",
                  "Scientific paper analysis and summarization",
                ],
              },
              {
                title: "Business & Productivity",
                items: [
                  "Code generation and debugging (GitHub Copilot)",
                  "Email automation and writing assistance",
                  "Customer service chatbots",
                ],
              },
              {
                title: "Creative Arts",
                items: [
                  "Game design and character generation",
                  "Storytelling and screenplay writing",
                  "Art style transfer and manipulation",
                ],
              },
              {
                title: "Education",
                items: [
                  "Personalized tutoring systems",
                  "Automated grading and feedback",
                  "Language learning applications",
                ],
              },
            ].map((section, idx) => (
              <Col xs={24} md={12} key={idx}>
                <Title level={5}>{section.title}</Title>
                <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
                  {section.items.map((item, itemIdx) => (
                    <li key={itemIdx}>{item}</li>
                  ))}
                </ul>
              </Col>
            ))}
          </Row>
        </Card>
      </Space>
    );
  };

  const renderGenerativeAI = () => {
    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card
          title={
            <span style={{ color: "#0071e3" }}>
              How Transformers Enable Generative AI
            </span>
          }
        >
          <Paragraph style={{ marginBottom: "16px" }}>
            Transformers are the backbone of modern generative AI because they
            can:
          </Paragraph>
          <Row gutter={[16, 16]}>
            {[
              {
                title: "1. Understand Context",
                description:
                  "Self-attention mechanisms allow the model to understand relationships between all parts of the input, enabling coherent and contextually appropriate generation.",
              },
              {
                title: "2. Handle Long Sequences",
                description:
                  "Transformers can process thousands of tokens at once, allowing them to generate long, coherent outputs like articles, code files, or detailed images.",
              },
              {
                title: "3. Learn Patterns",
                description:
                  "Trained on massive datasets, Transformers learn patterns in language, images, code, and more, enabling them to generate novel but realistic content.",
              },
              {
                title: "4. Transfer Knowledge",
                description:
                  "Pre-trained Transformers can be fine-tuned for specific tasks, making it efficient to create specialized AI models for different applications.",
              },
            ].map((item, idx) => (
              <Col xs={24} md={24} key={idx}>
                {/* <div style={{ padding: "16px", borderRadius: "8px", height: "100%" }}> */}
                <Title level={5}>{item.title}</Title>
                <Paragraph style={{ marginBottom: 0, fontSize: "14px" }}>
                  {item.description}
                </Paragraph>
                {/* </div> */}
              </Col>
            ))}
          </Row>
        </Card>
      </Space>
    );
  };

  const tabItems = [
    {
      key: "intro",
      label: "What is a Transformer?",
      children: renderIntro(),
    },
    {
      key: "how-it-works",
      label: "How It Works",
      children: renderHowItWorks(),
    },
    {
      key: "demo",
      label: "Interactive Demo",
      children: renderDemo(),
    },
    {
      key: "history",
      label: "History",
      children: renderHistory(),
    },
    {
      key: "use-cases",
      label: "Use Cases",
      children: renderUseCases(),
    },
    ,
    {
      key: "generative-ai",
      label: "Generative AI",
      children: renderGenerativeAI(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>Transformers</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          The technology powering modern AI
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="intro" items={tabItems} size="large" />
    </div>
  );
}
